import { useCallback, useState, useRef, useEffect } from 'react';
import useAuth from '../routes/login/AuthContext';
import { sendChatQuestion } from '../services/ChatApi';
import { useSelectedNetworkContext, useNetworkContext } from '../contexts/NetworkContext/context';

export interface ChatMessage {
  text?: string;
  error?: string;
}

export interface UseChatReturn {
  sendMessage: (message: string) => Promise<ChatMessage>;
  isAuthenticated: boolean;
  resetConversation: () => void;
}

export const useChat = (): UseChatReturn => {
  const { getAuthToken, isLoggedIn } = useAuth();
  const { selectedNetworkId } = useSelectedNetworkContext();
  const { networks } = useNetworkContext();
  const [previousResponseId, setPreviousResponseId] = useState<string | undefined>();
  const previousResponseIdRef = useRef<string | undefined>();

  // Keep ref in sync with state
  useEffect(() => {
    previousResponseIdRef.current = previousResponseId;
  }, [previousResponseId]);

  // Get the actual selected network ID, fallback to localStorage if context is 0
  const getSelectedNetworkId = useCallback(() => {
    if (selectedNetworkId && selectedNetworkId > 0) {
      return selectedNetworkId;
    }

    // Fallback to localStorage
    const storedNetworkId = localStorage.getItem("selectedNetworkId");
    return storedNetworkId ? parseInt(storedNetworkId, 10) : 0;
  }, [selectedNetworkId]);

  // Get the selected network name
  const getSelectedNetworkName = useCallback(() => {
    const actualNetworkId = getSelectedNetworkId();
    if (actualNetworkId === 0) {
      return undefined;
    }

    const selectedNetwork = networks.find(network => network.l1Id === actualNetworkId);
    return selectedNetwork?.l1Name;
  }, [networks, getSelectedNetworkId]);

  const sendMessage = useCallback(async (message: string): Promise<ChatMessage> => {
    try {
      if (!isLoggedIn()) {
        return { error: 'Please log in to use the chat feature.' };
      }

      const authToken = await getAuthToken();
      if (!authToken) {
        return { error: 'Authentication failed. Please log in again.' };
      }

      if (!message.trim()) {
        return { error: 'Please enter a message.' };
      }

      const actualNetworkId = getSelectedNetworkId();
      const selectedNetworkName = getSelectedNetworkName();
      const currentPreviousResponseId = previousResponseIdRef.current;

      const result = await sendChatQuestion(message, authToken, currentPreviousResponseId, actualNetworkId, selectedNetworkName);

      if (result.error) {
        return {
          error: result.error.message || 'Sorry, I encountered an error while processing your message. Please try again.'
        };
      } else if (result.response) {
        // Update the previousResponseId for the next request
        if (result.response.previousResponseId) {
          setPreviousResponseId(result.response.previousResponseId);
        }

        return {
          text: result.response.response || 'I received your message but couldn\'t generate a response.'
        };
      } else {
        return {
          error: 'Unexpected response format. Please try again.'
        };
      }

    } catch (error) {
      // console.error('Chat hook error:', error);
      return { 
        error: 'Sorry, I encountered an unexpected error. Please try again.' 
      };
    }
  }, [getAuthToken, isLoggedIn, getSelectedNetworkId, getSelectedNetworkName, setPreviousResponseId]);

  const resetConversation = useCallback(() => {
    setPreviousResponseId(undefined);
  }, [setPreviousResponseId]);

  return {
    sendMessage,
    isAuthenticated: isLoggedIn(),
    resetConversation,
  };
};
